import React, { useState, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import {
  ArrowLeft, Upload, FileText, Loader2, Save, Download,
} from 'lucide-react';
import { fetchClient, BACKEND_URL } from '@/lib/api';
import { LibraryItem } from './types';

interface PrecedentCreationViewProps {
  existingItem?: LibraryItem | null;
  onBack: () => void;
  onSave: () => Promise<void>;
}

export default function PrecedentCreationView({ existingItem, onBack, onSave }: PrecedentCreationViewProps) {
  const { backendToken } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [title, setTitle] = useState(existingItem?.title || '');
  const [description, setDescription] = useState(existingItem?.description || '');
  const [fileName, setFileName] = useState(existingItem?.fileName);
  const [downloading, setDownloading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load full data for existing item
  const existingId = existingItem?.id;
  React.useEffect(() => {
    if (!existingId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await fetchClient.GET("/api/v1/precedents/{precedent_id}", {
          params: { path: { precedent_id: existingId } },
        });
        if (cancelled || error || !data) return;
        setTitle(data.title || '');
        setDescription(data.description || '');
        setFileName(data.file_name || undefined);
      } catch {
        // Keep what we have from the list item
      }
    })();
    return () => { cancelled = true; };
  }, [existingId]);

  const handleUpload = async (selectedFile: File) => {
    setUploading(true);
    setSaveError(null);
    try {
      const token = backendToken || localStorage.getItem("backendToken");
      if (!token) return;

      const formData = new FormData();
      formData.append("file", selectedFile);

      const res = await fetch(`${BACKEND_URL}/api/v1/precedents/extract`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Upload failed (${res.status})`);
      }

      // Upload succeeded -- go back to library immediately
      await onSave();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async () => {
    if (!existingId || !fileName || downloading) return;
    setDownloading(true);
    try {
      const token = backendToken || localStorage.getItem("backendToken");
      const res = await fetch(`${BACKEND_URL}/api/v1/precedents/${existingId}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        setSaveError(`Download failed (${res.status})`);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Download failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleSave = async () => {
    if (!existingId || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const { error } = await fetchClient.PATCH("/api/v1/precedents/{precedent_id}", {
        params: { path: { precedent_id: existingId } },
        body: { title, description },
      });
      if (error) {
        setSaveError('Failed to save changes. Please try again.');
        return;
      }
      await onSave();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // New precedent: upload view
  if (!existingItem) {
    return (
      <div className="h-full flex flex-col bg-background">
        <div className="px-6 py-4 border-b border-border flex items-center gap-4 flex-shrink-0">
          <button onClick={onBack} className="p-2 hover:bg-secondary rounded-lg transition-colors text-muted-foreground">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-primary" />
            <h1 className="text-lg font-semibold text-foreground">
              New Precedent
            </h1>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center p-6">
          <div className="max-w-md w-full">
            <div
              className="border-2 border-dashed border-border rounded-lg p-10 text-center transition-colors"
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--primary)'; }}
              onDragLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.style.borderColor = 'var(--border)';
                const droppedFile = e.dataTransfer.files[0];
                if (droppedFile) handleUpload(droppedFile);
              }}
            >
              <Upload className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-base font-medium mb-1 text-foreground">
                Upload a document
              </h3>
              <p className="text-sm mb-6 text-muted-foreground">
                The pattern miner will extract structure, tone, and style for reuse.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".docx"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleUpload(f);
                }}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="px-5 py-2.5 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2 mx-auto transition-colors bg-primary"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Choose File
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-center mt-3 text-muted-foreground">
              Supported: Word (.docx)
            </p>
            {saveError && (
              <p className="text-sm text-red-600 text-center mt-3">{saveError}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Edit existing precedent: title + description only
  return (
    <div className="h-full flex flex-col bg-background">
      <div className="px-6 py-4 border-b border-border flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="p-2 hover:bg-secondary rounded-lg transition-colors text-muted-foreground">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-primary" />
            <h1 className="text-lg font-semibold text-foreground">
              Edit Precedent
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {saveError && (
            <span className="text-sm text-red-600">{saveError}</span>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors bg-primary hover:bg-[var(--accent-hover)] disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto p-6 space-y-5">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5 text-muted-foreground">
              Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-foreground bg-card border border-border"
              placeholder="Document title..."
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5 text-muted-foreground">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={6}
              className="w-full px-3 py-2.5 rounded-lg text-sm resize-vertical focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-foreground bg-card border border-border"
              placeholder="Brief description of this precedent..."
            />
            {fileName && (
              <button
                type="button"
                onClick={handleDownload}
                disabled={downloading}
                className="mt-2 inline-flex items-center gap-1.5 text-sm text-primary hover:underline cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                title={`Download ${fileName}`}
              >
                {downloading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                <span className="truncate max-w-md">{fileName}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
